import * as React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { makeStyles, Spinner, Text, Card, Button } from "@fluentui/react-components";
import { ShieldDismissRegular } from "@fluentui/react-icons";
import { supabase } from "./services/supabase";
import { LoginPage } from "./components/auth/LoginPage";
import { AdminLayout } from "./components/layout/AdminLayout";
import { UserListPage } from "./components/users/UserListPage";
import { ConfigPage } from "./components/config/ConfigPage";
import type { Session } from "@supabase/supabase-js";

const useStyles = makeStyles({
  loadingPage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
  },
  deniedPage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
  },
  deniedCard: {
    padding: "48px 40px",
    borderRadius: "16px",
    boxShadow:
      "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06)",
    border: "1px solid #f1f5f9",
    maxWidth: "400px",
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  deniedIcon: {
    fontSize: "48px",
    color: "#ef4444",
  },
  deniedTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
  },
  deniedText: {
    fontSize: "14px",
    color: "#64748b",
  },
});

type AuthState = "loading" | "unauthenticated" | "admin" | "denied";

export const App: React.FC = () => {
  const styles = useStyles();
  const [authState, setAuthState] = React.useState<AuthState>("loading");

  React.useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    // Listen for auth changes (login/logout)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSession = async (session: Session | null) => {
    if (!session) {
      setAuthState("unauthenticated");
      return;
    }

    // Verify admin access by calling the worker
    try {
      const ADMIN_API_URL = process.env.ADMIN_API_URL || "http://localhost:8787";
      const res = await fetch(`${ADMIN_API_URL}/api/stats`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (res.ok) {
        setAuthState("admin");
      } else if (res.status === 403) {
        setAuthState("denied");
      } else {
        // Token might be invalid
        setAuthState("denied");
      }
    } catch {
      // Worker not reachable — assume denied for safety
      setAuthState("denied");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthState("unauthenticated");
  };

  if (authState === "loading") {
    return (
      <div className={styles.loadingPage}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <LoginPage />;
  }

  if (authState === "denied") {
    return (
      <div className={styles.deniedPage}>
        <Card className={styles.deniedCard}>
          <ShieldDismissRegular className={styles.deniedIcon} />
          <Text className={styles.deniedTitle}>Access Denied</Text>
          <Text className={styles.deniedText}>
            Your account is not authorized to access the admin panel.
          </Text>
          <Button appearance="secondary" onClick={handleLogout}>
            Sign out
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Routes>
        <Route path="/users" element={<UserListPage />} />
        <Route path="/config" element={<ConfigPage />} />
        <Route path="*" element={<Navigate to="/users" replace />} />
      </Routes>
    </AdminLayout>
  );
};
